import { patientDisplayName } from 'utils/orders';
import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, Grid, Paper, Typography } from '@mui/material';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import PatientSummary from 'components/order-flow/shared/patient-summary.component';
import DentistSummary from 'components/order-flow/shared/dentist-summary.component';
import InternalSummary from 'components/order-flow/shared/internal-summary.component';
import SampleSummary from 'components/order-flow/shared/sample-summary.component';
import DeliverySummary from './delivery-summary/delivery-summary.component';
import ConstructionsSummary from './construction-summary/construction-summary.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { createOrderTemplate, modifyOrderTemplate } from 'apollo/templates/order';
import { LoadingButton } from '@mui/lab';
import {
    buildOrderPdfFileName,
    downloadOrderPdf,
} from 'components/order-details/main-order-view/document-print-actions/files';
import { Order } from 'models/order';
import { labOrderToOrderInput } from 'components/order-flow/shared/order-input-helper';
import { CurrencyContext } from 'context/currency';
import { OrderInputType } from 'models/order-input/shared';
import FileGrid from 'components/shared/file-upload-area/file-grid.component';
import RestorationsTable from 'components/scanner-cases/shared/restorations-table/restorations-table.component';

type ConfirmStepProps = {
    onBackClick: () => void;
};
const ConfirmStep: FC<ConfirmStepProps> = ({ onBackClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order } = useContext(LabOrderContext);
    const { currency } = useContext(CurrencyContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const [worksheetChecked, setWorksheetChecked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isResend, setIsResend] = useState(false);
    const search = useLocation().search;
    const hasInternalInfo = !!(order.internalOrderId || order.panNumber);

    const navigate = useNavigate();

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        setIsResend(queryParams.get('isResend') === 'true' || false);
    }, [search]);

    const [createOrder] = useMutation(createOrderTemplate, {
        onCompleted: async ({ createOrder }) => {
            if (worksheetChecked) {
                await downloadWorksheet(createOrder);
            } else {
                setLoading(false);
                navigate(`/order-details/${createOrder._id}/main-view`);
            }
        },
        onError(error) {
            setLoading(false);
            showBoundary(error);
        },
    });

    const [modifyOrder] = useMutation(modifyOrderTemplate, {
        onCompleted: () => {
            setLoading(false);
            navigate(`/order-details/${order._id}/main-view`);
        },
        onError(error) {
            setLoading(false);
            showBoundary(error);
        },
    });

    const downloadWorksheet = async (order: Order) => {
        try {
            const currentDate = new Date().toLocaleDateString();
            // A patient-less order (DD-3494) omits the patient segment from the file name.
            const fileNameParts = [order.orderId, patientDisplayName(order.patient) || undefined, currentDate].filter(
                Boolean,
            );
            const fileName = `${fileNameParts.join('_')}_work.pdf`;
            await downloadOrderPdf(order._id, 'WORK_SHEET_V2_PDF', fileName);
        } catch (error) {
            showBoundary(error);
        } finally {
            setLoading(false);
            setWorksheetChecked(false);
            navigate(`/order-details/${order._id}/main-view`);
        }
    };

    // A new order, or a scanner-case order: the case id travels on the input. A copy or a
    // warranty completes the draft the details page created for it.
    const handleCreateOrderClick = () => {
        setLoading(true);
        createOrder({ variables: { order: labOrderToOrderInput(order, currency) } });
    };

    const handleUpdateOrderClick = () => {
        setLoading(true);
        modifyOrder({ variables: { orderId: order._id, order: labOrderToOrderInput(order, currency), isResend } });
    };

    const handleCompleteDraftClick = () => {
        setLoading(true);
        createOrder({ variables: { order: labOrderToOrderInput(order, currency), draftId: order._id } });
    };

    const getActionButton = () => {
        switch (order.inputType) {
            case OrderInputType.MODIFY:
                return (
                    <LoadingButton
                        loading={loading}
                        disabled={loading}
                        type="button"
                        onClick={handleUpdateOrderClick}
                        variant="contained"
                        data-test="confirm-step.update-order-btn"
                    >
                        {translate('orders:UPDATE_ORDER')}
                    </LoadingButton>
                );
            case OrderInputType.NEW:
                return (
                    <LoadingButton
                        loading={loading}
                        disabled={loading}
                        type="button"
                        onClick={handleCreateOrderClick}
                        variant="contained"
                        data-test="confirm-step.register-order-btn"
                    >
                        {translate('orders:REGISTER_ORDER')}
                    </LoadingButton>
                );
            case OrderInputType.COPY:
                return (
                    <LoadingButton
                        loading={loading}
                        disabled={loading}
                        type="button"
                        onClick={handleCompleteDraftClick}
                        variant="contained"
                        data-test="confirm-step.copy-order-btn"
                    >
                        {translate('orders:COPY_ORDER')}
                    </LoadingButton>
                );
            case OrderInputType.WARRANTY:
                return (
                    <LoadingButton
                        loading={loading}
                        disabled={loading}
                        type="button"
                        onClick={handleCompleteDraftClick}
                        variant="contained"
                        data-test="confirm-step.register-warranty-btn"
                    >
                        {translate('orders:REGISTER_ORDER')}
                    </LoadingButton>
                );
            default:
                return null;
        }
    };

    return (
        <Fragment>
            <Box mb={2}>
                <Typography variant="h1">{translate('orders:CONFIRM')}</Typography>
            </Box>
            <Box mb={2}>
                <Divider />
            </Box>

            <Box mb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={hasInternalInfo ? 4 : 6}>
                        <Paper variant="outlined" sx={{ height: '100%' }}>
                            <Box p={2}>
                                <Typography variant="h4">{translate('orders:PATIENT')}</Typography>
                                <Box mt={1}>
                                    <PatientSummary patient={order.patient} />
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={hasInternalInfo ? 4 : 6}>
                        <Paper variant="outlined" sx={{ height: '100%' }}>
                            <Box p={2}>
                                <Typography variant="h4">{translate('common:CUSTOMER')}</Typography>
                                <Box mt={1}>
                                    <DentistSummary dentist={order.dentist} clinic={order.customer} />
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    {hasInternalInfo ? (
                        <Grid item xs={12} md={4}>
                            <Paper variant="outlined" sx={{ height: '100%' }}>
                                <Box p={2}>
                                    <Typography variant="h4">{translate('orders:INTERNAL_SECTION')}</Typography>
                                    <Box mt={1}>
                                        <InternalSummary
                                            internalOrderId={order.internalOrderId}
                                            panNumber={order.panNumber}
                                            panNumberDescription={order.panNumberDescription}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    ) : undefined}
                </Grid>
            </Box>

            <Box mb={2}>
                <ConstructionsSummary />
            </Box>

            {order.scannerCase ? (
                <Box mb={2}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h4">
                            {translate(`scan-cases:${order.scannerCase?.source}_INFO`)}
                        </Typography>
                        <RestorationsTable restorations={order.scannerCase.restorations} />
                    </Paper>
                </Box>
            ) : undefined}

            <Box mb={2}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <DeliverySummary order={order} />
                    </Box>
                </Paper>
            </Box>

            <Box mb={2}>
                <FileGrid files={order.files} />
            </Box>

            <Box>
                <Paper variant="outlined" sx={{ height: '100%' }}>
                    <Box p={2}>
                        <Typography variant="h4">{translate('orders:SAMPLE_AND_PACKAGE_CONTENT')}</Typography>
                        <Box mt={1}>
                            <SampleSummary sample={order.sample} />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <Box display="flex" mt={2}>
                <Box flexGrow={1} textAlign="left">
                    <Button type="button" onClick={onBackClick} data-test="confirm-step.back">
                        {translate('common:BACK')}
                    </Button>
                </Box>
                <Box>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={worksheetChecked}
                                    onChange={(e) => setWorksheetChecked(e.target.checked)}
                                    data-test="confirm-step.worksheet-checkbox"
                                />
                            }
                            label={translate('orders:DOWNLOAD_WORKSHEET')}
                        />
                    </FormGroup>
                </Box>
                <Box>{getActionButton()}</Box>
            </Box>
        </Fragment>
    );
};

export default ConfirmStep;
